// The audit trail from `ticket_events` — append-only, enforced by trigger, so this is
// a record rather than a view of current state (docs/API.md §3.2).
//
// It is rendered apart from the message thread on purpose: interleaving "status changed
// to pending" with the messages themselves would blur the one distinction on this
// screen that has to stay sharp (internal note vs customer-facing reply).
//
// Values arrive as text ids — an assignee change carries a user id, not a name — so
// names are resolved from lookups the ticket already supplies, and fall back to the raw
// value rather than pretending the event is unreadable.

import { STATUS_LABELS, PRIORITY_LABELS, formatDateTime } from './format.js';
import { TYPE_LABELS } from '../../pages/tickets/ticket-type.js';

const MESSAGE_DIRECTIONS = {
  internal: 'added an internal note',
  outbound: 'sent a customer-facing reply',
  inbound: 'received a customer email',
};

// Both Zendesk push events carry `zendesk_outbox.kind` in `field` — 'comment' or
// 'fields' (src/zendesk/outbox.js) — and an agent cares about the difference: a comment
// is words a Zendesk reader is waiting for, field changes are bookkeeping.
const ZENDESK_PUSH_KINDS = {
  comment: 'a comment',
  fields: 'field changes',
};

function statusLabel(value) {
  if (!value) return 'none';
  return STATUS_LABELS[value] ?? value;
}

function describe(event, lookups) {
  const { userName, customerName, namedStatusName } = lookups;
  const { from_value: from, to_value: to } = event;

  switch (event.event_type) {
    case 'created':
      return `created the ticket as ${statusLabel(to)}`;
    case 'status_changed':
      return `changed status from ${statusLabel(from)} to ${statusLabel(to)}`;
    case 'named_status_changed':
      return to
        ? `set the named status to ${namedStatusName(to)}`
        : `cleared the named status${from ? ` (${namedStatusName(from)})` : ''}`;
    case 'priority_changed':
      return `changed priority from ${PRIORITY_LABELS[from] ?? from ?? 'none'} to ${
        PRIORITY_LABELS[to] ?? to ?? 'none'
      }`;
    case 'type_changed':
      return `changed type from ${TYPE_LABELS[from] ?? from ?? 'none'} to ${
        TYPE_LABELS[to] ?? to ?? 'none'
      }`;
    case 'assignee_changed':
      return to ? `assigned the ticket to ${userName(to)}` : 'unassigned the ticket';
    case 'tag_added':
      return `added the tag ${to}`;
    case 'tag_removed':
      return `removed the tag ${from}`;
    case 'message_added':
      return MESSAGE_DIRECTIONS[to] ?? 'added a message';
    case 'cc_added':
      return `added ${customerName(to)} as a CC`;
    // REMOVING a CC matters more than adding one and is why the detail screen can do
    // it: a CC is a standing subscription to every future agent reply, and
    // docs/EMAIL.md §2.3 reads `ticket_ccs` back as proof an address belongs on the
    // thread. Taking somebody off is the security-relevant half, so it gets a row.
    case 'cc_removed':
      return `removed ${customerName(from)} as a CC`;
    case 'follower_added':
      return `added ${userName(to)} as a follower`;
    case 'follower_removed':
      return `removed ${userName(from)} as a follower`;
    // Related tickets (docs/API.md §3.7). Both tickets carry the row; each names the
    // OTHER by number, which is why the value is shown as a ticket number here.
    case 'ticket_linked':
      return `linked ticket #${to}`;
    case 'ticket_unlinked':
      return `removed the link to ticket #${from}`;
    // Written on the PARENT when a child is created from it (§3.3).
    case 'child_created':
      return `split off child ticket #${to}`;
    case 'queue_changed':
      // Written by routing as well as by an agent edit, and the actor is what tells
      // them apart — a null actor is the router (src/routing/after-create.js).
      return to ? `moved the ticket to another queue` : 'took the ticket out of its queue';
    case 'form_changed':
      return 'changed the ticket form';
    // The requester is who every reply is addressed to, so this is a redirection of
    // the correspondence rather than a field edit, and it reads that way.
    case 'requester_changed':
      return `changed the caller to ${customerName(to)}`;
    case 'organization_changed':
      return to
        ? 'filed the ticket under a different organization'
        : 'removed the ticket from its organization';
    // Written when the send guard refuses a reply by design (soak period: the CRM may
    // email only an allowlist). `field` is the message id; `to` is the reason. No
    // address is stored, so none can be shown.
    case 'mail_send_refused':
      return to === 'allowlist'
        ? 'saved a reply without emailing it: sending is limited to test addresses'
        : 'saved a reply that was not emailed';
    // Written by the send path when it cannot prove a reply did NOT reach the customer
    // (src/mail/outbound.js). `field` carries the message id here rather than a column
    // name — the one event in this vocabulary that does, because the row has to point
    // at a message and `ticket_events` has no column for that. The message itself
    // carries the loud version of this; the audit trail carries the timestamp and the
    // Message-ID, which is what someone reconciling against Sent Items needs.
    case 'mail_delivery_unconfirmed':
      return to === 'sent_unrecorded'
        ? `emailed a reply${from ? ` as ${from}` : ''} that could not be recorded. Do not resend it`
        : `handed a reply${from ? ` (${from})` : ''} to Microsoft and got no answer, so it may have been emailed`;
    // ---------------------------------------------------------------------
    // The Zendesk bridge (docs/ZENDESK-BRIDGE.md). Every one of these is written with
    // `actor_user_id` null, so they render as "System" — the bridge is not a person and
    // must not read as one.
    // ---------------------------------------------------------------------
    // Written once, when the pull creates the CRM ticket from a Zendesk one
    // (src/zendesk/pull.js). The Zendesk NUMBER is what an agent has in front of them
    // when somebody asks about the other desk, so it is the value shown.
    case 'zendesk_linked':
      return `linked this ticket to Zendesk #${to}`;
    // The push succeeded (src/zendesk/push.js). `to_value` is the Zendesk audit id,
    // which identifies the change on their side and means nothing on ours, so it is not
    // shown — the fact an agent needs is that their words reached the other desk.
    case 'zendesk_pushed':
      return `sent ${ZENDESK_PUSH_KINDS[event.field] ?? 'a change'} to Zendesk`;
    // The push gave up after the retry budget. `to_value` carries the last error, which
    // is an operator's string and stays where operators read it (`zendesk_outbox`, and
    // the `zendesk.push.exhausted` alert). What belongs in an agent's audit trail is
    // that Zendesk never got this, because the CRM did — the two desks now disagree.
    case 'zendesk_push_failed':
      return `could not send ${ZENDESK_PUSH_KINDS[event.field] ?? 'a change'} to Zendesk `
        + 'after repeated attempts. Zendesk does not have this change';
    // A Zendesk end user with no email address at all — phone-only callers are real, and
    // the ticket is kept rather than dropped (spec §4.3). `to_value` is the invented
    // address, shown because it is how the ticket is found again when somebody works
    // through the list and merges these into the real customer.
    case 'zendesk_requester_placeholder':
      return `filed the ticket under a placeholder caller (${to}) — the Zendesk user has `
        + 'no email address';
    // Written by the pull when a subject is edited in Zendesk. It is NOT in
    // `applyChanges` and the PATCH endpoint does not accept a subject, so today this row
    // can only have come from the bridge; it is written plainly here so that stops being
    // true without this file needing to change.
    case 'subject_changed':
      return to ? `changed the subject to "${to}"` : 'cleared the subject';
    default:
      // The vocabulary can grow server-side before this file knows about it; showing
      // the raw event beats dropping a row out of an audit trail.
      return `${event.event_type}${event.field ? ` (${event.field})` : ''}: ${from ?? '-'} → ${to ?? '-'}`;
  }
}

/**
 * @param {object} props
 * @param {Array} props.events
 * @param {Map} props.usersById        id → display_name, from GET /api/users. May be empty.
 * @param {Map} props.customersById    id → name, from the ticket's requester and CCs.
 * @param {Map} props.namedStatusesById id → name. May be empty.
 */
export default function ActivityLog({ events, usersById, customersById, namedStatusesById }) {
  const lookups = {
    userName: (id) => usersById.get(id) ?? `user ${id}`,
    customerName: (id) => customersById.get(id) ?? `customer ${id}`,
    namedStatusName: (id) => namedStatusesById.get(id) ?? `status ${id}`,
  };

  if (events.length === 0) return null;

  return (
    <details className="rounded-lg border border-line bg-surface">
      <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-ink">
        Activity ({events.length})
      </summary>
      <ol className="border-t border-line px-4 py-3 text-sm">
        {events.map((event) => (
          <li key={event.id} className="flex flex-wrap gap-x-2 py-1 text-ink-secondary">
            <span className="font-medium text-ink">
              {event.actor?.display_name ?? 'System'}
            </span>
            <span>{describe(event, lookups)}</span>
            <span className="ml-auto text-xs text-ink-muted" title={event.created_at}>
              {formatDateTime(event.created_at)}
            </span>
          </li>
        ))}
      </ol>
    </details>
  );
}
